import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { type BaseQueryFn, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type { ESBBaseError } from '@appTypes/esb/errors';
import { IS_LOCAL_ENV, IS_QA_ENV } from '@config/config';
import { authTokenExpired } from '@modules/shared/duck/actionsTyped';

const rawBaseQuery = (baseUrl: string, ...args: unknown[]) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, api) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const jwt = (api.getState() as any).user?.loginData?.token;
      if (jwt) {
        headers.set('Authorization', `Bearer ${jwt}`);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const testJWT = (window as unknown as any).testJWT;
      if (testJWT && (IS_QA_ENV || IS_LOCAL_ENV)) {
        headers.set('Authorization', `Bearer ${testJWT}`);
      }
      headers.set('x-esb-url', btoa(window.location.href));
      return headers;
    },
    ...args,
  });

/**
 * It automatically adds the Authorization header with the current
 * value in the store set at `store.user.loginData.token`
 */
export const dynamicBaseQuery =
  (
    getBaseUrl: (...args: BaseQueryFn['arguments']) => string,
  ): ReturnType<typeof fetchBaseQuery> =>
  async (args, api, extraOptions) => {
    const baseUrl = getBaseUrl(args, api, extraOptions);

    if (!baseUrl) {
      return {
        error: {
          status: 400,
          statusText: 'Bad Request',
          data: `No baseUrl found ${baseUrl}`,
        },
      };
    }

    return rawBaseQuery(baseUrl)(args, api, extraOptions);
  };

/**
 * Log a warning and show a toast!
 * Taken from: https://redux-toolkit.js.org/rtk-query/usage/error-handling
 */
export const rtkTokenExpiredCheck: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_api: MiddlewareAPI) => next => action => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (!isRejectedWithValue(action)) return next(action);

    // Got rejected API response
    const _action = action as {
      payload: { status?: number; data?: ESBBaseError };
    };
    const isRevoked =
      _action.payload?.status === 401 &&
      _action.payload?.data?.errorCode === 'C401' &&
      _action.payload?.data?.errorDesc === 'Revoked token';

    const isInvalid =
      _action.payload?.status === 401 &&
      _action.payload?.data?.errorCode === 'M401' &&
      _action.payload?.data?.errorDesc === 'Wrong JWT Signature';

    const isInvalidJWT = isRevoked || isInvalid;

    // Try to logout
    if (isInvalidJWT) {
      const response = next(action);
      _api.dispatch(authTokenExpired({ token: '' }));
      return response;
    }

    // Continue by default
    return next(action);
  };
